import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { usePlatform } from "hooks/usePlatform";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { formatCurrency } from "toolbox/Money";

import { getExternalTransactions, isAdjustment } from "./OrderHistoryPage";

export const externalTransactionDetailsLoader = (async ({ params: { externalTransactionId } }) => {
  if (!externalTransactionId) throw new Error("Missing parameter :externalTransactionId");

  const transactions = await queryClient.fetchQuery({ ...getExternalTransactions.keyed() });

  if (!transactions.some((t) => t.id === externalTransactionId)) {
    throw redirect("..");
  }

  return { transactions, externalTransactionId };
}) satisfies LoaderFunction;

type LoaderData = Awaited<ReturnType<typeof externalTransactionDetailsLoader>>;

const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between">
      <div className="text-bold1 capitalize text-primary">{label}</div>
      <div className="text-body1">{value}</div>
    </div>
  );
};

export const ExternalTransactionDetailsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { platform } = usePlatform();
  const loaderData = useLoaderData() as LoaderData;

  const { data: transaction } = useQuery({
    ...getExternalTransactions.keyed(),
    initialData: loaderData.transactions,
    select: (transactions) => transactions.find((t) => t.id === loaderData.externalTransactionId),
  });

  if (!transaction) {
    return (
      <div className="relative h-full bg-one">
        <TopNav
          startIconSVG={ChevronLeft}
          showHeaderItems={false}
          onStartIconClick={() => navigate(-1)}
        >
          <div className="flex justify-center text-sub2">
            {t("externalTransactionDetails.notFound")}
          </div>
        </TopNav>
        <div className="p-5 text-center text-body1 text-secondary">
          {t("externalTransactionDetails.transactionNotFound")}
        </div>
      </div>
    );
  }

  const adjustment = isAdjustment(transaction);
  const formattedAmount = formatCurrency(Math.abs(transaction.amount));
  const displayAmount = transaction.amount < 0 ? `−${formattedAmount}` : formattedAmount;

  return (
    <div className="relative h-full bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {adjustment
            ? t("externalTransactionDetails.adjustmentDetailsHeader")
            : t("externalTransactionDetails.orderDetailsHeader")}
        </div>
      </TopNav>
      <div className="p-5">
        <div className="flex flex-col gap-5 rounded-lg bg-white px-7 py-6 shadow-subtle">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <div className="text-bold1 capitalize text-primary">
                {adjustment
                  ? t("externalTransactionDetails.adjustment")
                  : t("externalTransactionDetails.orderNumber", {
                      number: transaction.externalReferenceId ?? transaction.id,
                    })}
              </div>
              <div className="text-body1">{dayjs(transaction.transactionDate).format("L")}</div>
            </div>

            {!adjustment && (
              <DetailRow
                label={t("externalTransactionDetails.transactionSource")}
                value={transaction.source}
              />
            )}

            <DetailRow
              label={
                adjustment
                  ? t("externalTransactionDetails.adjustmentTotal")
                  : t("externalTransactionDetails.transactionTotal")
              }
              value={displayAmount}
            />

            {adjustment && transaction.description && (
              <div className="mt-1 flex flex-col gap-1">
                <div className="text-bold1 text-primary">
                  {t("externalTransactionDetails.reason")}
                </div>
                <div className="text-body1 text-secondary">{transaction.description}</div>
              </div>
            )}
          </div>

          <div className="border-t border-light-grey pt-4 text-body2 text-secondary">
            {adjustment
              ? t("externalTransactionDetails.adjustmentDisclaimer", {
                  practiceName: platform?.name ?? t("externalTransactionDetails.yourPractice"),
                })
              : t("externalTransactionDetails.orderDisclaimer")}
          </div>
        </div>
      </div>
      <div className="relative inset-x-0 bottom-0 mx-auto p-4 pb-9 desk:w-full sm:w-[390px]">
        <RepeatMDFooter />
      </div>
    </div>
  );
};
